import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { getDocs, query, where } from "firebase/firestore";
import { auth } from "../../firebase";
import Window from "../../components/Window";
import Icon from "../../components/Icon";
import { Postcard } from "../../components/Postcards";
import { useBlocks } from "../../blocks";
import {
  POST_CATEGORIES,
  Piece,
  deliveryName,
  getMyPieces,
  piecesCol,
  postmark,
  useIsEditor,
} from "../../post";
import "../../styles.css";

// The PenPal Post: dispatches from the team, and members' writing that the
// editors have delivered.
export default function PostFront() {
  const [user] = useAuthState(auth);
  const editor = useIsEditor(user?.uid);
  const { ids: blocked } = useBlocks();
  const [pieces, setPieces] = useState<Piece[] | null>(null);
  const [mine, setMine] = useState<Piece[]>([]);
  const [category, setCategory] = useState("");

  useEffect(() => {
    getDocs(query(piecesCol, where("status", "==", "delivered")))
      .then((snap) =>
        setPieces(
          snap.docs
            .map((d) => ({ id: d.id, ...(d.data() as Omit<Piece, "id">) }))
            .filter((p) => !p.removed && !(p.uid && blocked.has(p.uid)))
            .sort((a, b) => (b.deliveredAt?.toMillis() ?? 0) - (a.deliveredAt?.toMillis() ?? 0))
        )
      )
      .catch((err) => {
        console.error(err);
        setPieces([]);
      });
  }, [blocked]);

  useEffect(() => {
    if (user) getMyPieces(user.uid).then(setMine).catch(() => setMine([]));
  }, [user]);

  const dispatches = (pieces || []).filter((p) => p.kind === "dispatch").slice(0, 3);
  const writing = (pieces || []).filter((p) => p.kind !== "dispatch" && (!category || p.category === category));
  const now = deliveryName({ toDate: () => new Date() });

  const card = (p: Piece) => (
    <Postcard
      key={p.id}
      to={`/post/${p.id}`}
      subject={p.title}
      message={p.body}
      cw={p.cw}
      color={p.color}
      categories={p.category ? [p.category] : undefined}
      from={
        p.kind === "dispatch"
          ? "— the Post Office ✉"
          : p.anonymous
          ? "— anonymous ✿"
          : `— @${p.username}`
      }
      footnote={p.deliveredAt ? `postmarked ${postmark(p.deliveredAt)}` : undefined}
    />
  );

  return (
    <>
      <Window title="the_penpal_post.htm" tone="pink">
        <div className="text-center">
          <p className="font-pixel text-xs tracking-widest text-ink-soft">
            ✉ {now.toUpperCase()} DELIVERY ✉
          </p>
          <h1 className="window-heading text-4xl">The PenPal Post</h1>
          <p className="window-sub">
            Our little mail service and journal: news from the Post Office, and
            writing from members of the network. Got something to share? Drop it
            in the mailbox.
          </p>
          <div className="btn-row justify-center">
            <Link to="/post/submit" className="btn">
              <Icon name="mail" size={16} /> Drop something in the mailbox
            </Link>
            {editor && (
              <Link to="/post/sorting" className="btn btn-plain">
                <Icon name="inbox" size={16} /> Sorting room
              </Link>
            )}
          </div>
        </div>
      </Window>

      {dispatches.length > 0 && (
        <Window title="from_the_post_office.txt" tone="tan">
          <div className="grid gap-5 pt-2 sm:grid-cols-2 xl:grid-cols-3">{dispatches.map(card)}</div>
        </Window>
      )}

      {mine.length > 0 && (
        <Window title={`my_submissions (${mine.length})`} tone="mint">
          <ul className="flex flex-col gap-2">
            {mine.map((p) => (
              <li key={p.id} className="flex flex-wrap items-center gap-2 text-sm">
                <Link to={`/post/${p.id}`} className="font-bold">
                  {p.title}
                </Link>
                <span className={`chip${p.status === "delivered" ? " on" : ""}`}>
                  {p.status === "delivered"
                    ? "delivered ✿"
                    : p.status === "returned"
                    ? "returned to sender"
                    : "waiting to be read"}
                </span>
                {p.status === "returned" && p.editorNote && (
                  <span className="w-full text-xs text-ink-soft">Editor's note: "{p.editorNote}"</span>
                )}
              </li>
            ))}
          </ul>
        </Window>
      )}

      {pieces !== null && writing.length + (category ? 1 : 0) > 0 && (
        <div className="flex flex-wrap items-end gap-3">
          <label className="field min-w-[12rem]">
            <span className="field-label">Category</span>
            <select className="input" value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="">Everything</option>
              {POST_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      {pieces === null ? (
        <p className="empty">Sorting the mail...</p>
      ) : writing.length === 0 ? (
        <div className="window">
          <p className="empty">
            <span className="empty-icon">
              <Icon name="mail" size={36} tone="pink" pop />
            </span>
            {category ? "Nothing in this category yet." : "No writing has been delivered yet. Be the first to send some in!"}
          </p>
        </div>
      ) : (
        <div className="grid gap-5 pt-2 sm:grid-cols-2 xl:grid-cols-3">{writing.map(card)}</div>
      )}
    </>
  );
}
